import { queryOptions } from '@tanstack/react-query';

import * as spacesApi from './spaces.api';

/** Spaces change rarely — keep them longer than the app default. */
const CATALOG_STALE_TIME = 5 * 60_000;

export const spaceKeys = {
  all: ['spaces'] as const,
  list: () => [...spaceKeys.all, 'list'] as const,
  detail: (slug: string) => [...spaceKeys.all, 'detail', slug] as const,
  availability: (resourceId: string, date: string) =>
    [...spaceKeys.all, 'availability', resourceId, date] as const,
  deskAvailability: (
    resourceId: string,
    startDate: string,
    deskPackageItemId?: string,
    startMinute?: number,
    endMinute?: number,
  ) =>
    [
      ...spaceKeys.all,
      'desk-availability',
      resourceId,
      startDate,
      deskPackageItemId ?? 'day',
      startMinute ?? 'all-day',
      endMinute ?? 'all-day',
    ] as const,
};

export const spaceQueries = {
  list: () =>
    queryOptions({
      queryKey: spaceKeys.list(),
      queryFn: spacesApi.listSpaces,
      staleTime: CATALOG_STALE_TIME,
    }),
  detail: (slug: string) =>
    queryOptions({
      queryKey: spaceKeys.detail(slug),
      queryFn: () => spacesApi.getSpace(slug),
      staleTime: CATALOG_STALE_TIME,
    }),
  /** One date's free hours. Always read fresh: someone else can book in the meantime. */
  availability: (resourceId: string, date: string) =>
    queryOptions({
      queryKey: spaceKeys.availability(resourceId, date),
      queryFn: () => spacesApi.getAvailability(resourceId, date),
      staleTime: 0,
    }),
  /** Desk labels and availability for painting a public floor map. */
  deskAvailability: (
    resourceId: string,
    startDate: string,
    deskPackageItemId?: string,
    startMinute?: number,
    endMinute?: number,
  ) =>
    queryOptions({
      queryKey: spaceKeys.deskAvailability(
        resourceId,
        startDate,
        deskPackageItemId,
        startMinute,
        endMinute,
      ),
      queryFn: () =>
        spacesApi.getDeskAvailability(
          resourceId,
          startDate,
          deskPackageItemId,
          startMinute,
          endMinute,
        ),
      staleTime: 0,
    }),
};
